import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/apiConfig';
import { format, addDays } from 'date-fns';

const Container = styled.div`
  max-width: 900px;
  margin: 10px auto;
  background: white;
  padding: 10px 32px 10px 32px;
  font-family: serif;
  color: #222;
  @media print {
    margin:  0 !important;
  }
`;
const Logo = styled.img`
  width: 600px;
  margin-bottom: 0;
`;
const Title = styled.h2`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 30px 0;
  letter-spacing: 1px;
  @media print {
    margin: 0 0 10px 0 !important;
    text-align: center !important;
  }
`;
const SubTitle = styled.div`
  text-align: center;
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 18px;
  color: gray;
  font-weight: bold;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 18px;
  @media print {
    width: 100% !important;
    table-layout: fixed !important;
    margin-bottom: 10px !important;
  }
`;
const Th = styled.th`
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 16px;
  @media print {
    font-size: 14px;
  }
`;
const Td = styled.td`
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 16px;
  @media print {
    font-size: 14px;
  }
`;
const SectionTitle = styled.div`
  font-weight: 600;
  margin: 18px 0 6px 0;
  font-size: 1.05rem;
`;
const Row = styled.tr``;
const Cell = styled.td``;
const SignatureBox = styled.div`
  min-height: 48px;
  margin-bottom: 4px;
  background-color: white;
`;
const Notice = styled.div`
  font-size: 0.92rem;
  color: 'black;
padding-left: 30px;
padding-right: 30px;
font-style: italic;
  `;
const Loading = styled.div`
  text-align: center;
  padding: 40px;
`;
const LoadingField = styled.span`
  color: #888;
  font-style: italic;
  font-size: 0.9em;
`;
const ErrorMsg = styled.div`
  color: #b00;
  text-align: center;
  margin: 30px 0;
`;
const DownloadButton = styled.button`
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  margin: 30px auto 0 auto;
  display: block;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(44,62,80,0.08);
  &:hover {
    background: #34495e;
    box-shadow: 0 4px 12px rgba(44,62,80,0.2);
    transform: translateY(-2px);
    color: white;
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(44,62,80,0.15);
  }
  @media print {
    display: none;
  }
`;

const ToggleSignatureButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'showSignature'
})`
  background: ${props => props.showSignature ? '#2c3e50' : '#e74c3c'};
  color: white;
  border: none;
  padding: 0 20px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 10px 0 0;
  cursor: pointer;
  height: 40px;
  margin-top: auto;
  transition: background 0.2s;
  box-shadow: ${props => props.showSignature ? '0 2px 8px rgba(44,62,80,0.2)' : '0 2px 8px rgba(231,76,60,0.2)'};
  &:hover {
    background: ${props => props.showSignature ? '#1a2533' : '#c0392b'};
  }
  @media print {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 30px 0 0 0;
  @media print {
    display: none;
  }
`;

const EditableField = styled.div`
  cursor: pointer;
  min-height: 20px;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  @media print {
    cursor: default;
    &:hover {
      background-color: transparent;
    }
  }
`;

const EditInput = styled.input`
  width: 100%;
  padding: 4px 6px;
  border: 2px solid #007bff;
  border-radius: 3px;
  font-size: 0.9rem;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #0056b3;
  }
`;

const AttendanceModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  
  @media print {
    display: none;
  }
`;

const AttendanceModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  min-width: 400px;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 1.2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 2px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TimeInputContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const CancelButton = styled(ModalButton)`
  background: #6c757d;
  color: white;
  
  &:hover {
    background: #5a6268;
  }
`;

const SubmitButton = styled(ModalButton)`
  background: #28a745;
  color: white;
  
  &:hover {
    background: #218838;
  }
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  margin: 30px auto 0 auto;
  display: block;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  @media print {
    display: none;
  }
`;

const UpdateAttendanceButton = styled.button`
  background: #1c7ab9;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 2px 8px #1c7ab9;
  &:hover {
    background:rgb(12, 79, 124);
  }
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  @media print {
    display: none;
  }
`;

const BASE_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const DAY_NAME_TO_DOW = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6
};

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  margin-bottom: 8px;
  @media print {
  }
`;

const AttendanceTd = styled.td`
  border: 1px solid #333;
  padding: 3px 8px;
  font-size: 12px;
  @media print {
    font-size: 11px !important;
  }
`;
const AttendanceTh = styled.th`
  border: 1px solid #333;
  // the background color does not print in the print view
  background:#e7e6e6 !important;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  padding: 3px 0;
  font-size: 12px;
  font-weight: bold;
  min-height: 20px;
  height: 25px;
 @media print {
    min-width: 140px !important;
background:#e7e6e6 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

  }
`;

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/** Used for auto signature date: last day with real marks, not a later Absent-only row. */
function recordHasMarkedAttendanceForSignature(record) {
  if (!record) return false;
  const status = String(record.status || '').trim().toLowerCase();
  if (status === 'present') {
    return !!(record.class_joined_at && record.class_left_at);
  }
  if (status === 'on leave' || status === 'leave') {
    return !!(record.leave_reason && String(record.leave_reason).trim());
  }
  if (status === 'others') {
    return !!(record.notes && String(record.notes).trim());
  }
  return false;
}

// Add a global print style for table row page-break
const PrintStyle = () => (
  <style>{`
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      img.signature-img {
        max-height: 42px !important;
        height: auto !important;
        width: auto !important;
        object-fit: contain !important;
        image-rendering: auto !important;
        -webkit-filter: none !important;
        filter: none !important;
        mix-blend-mode: normal !important;
      }
      html, body, #root, .App {
        width: 100vw !important;
        max-width: 100vw !important;
        min-width: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow-x: visible !important;
      }
      
      .Container {
        max-width: 85% !important;
        width: 85% !important;
        margin: 10px auto !important;
        padding: 20px !important;
        background: white !important;
        box-shadow: none !important;
        border: 2px solid #333 !important;
        overflow: visible !important;
      }
      
      /* Single-line grid: each cell draws top+left only; table draws right+bottom */
      table {
        width: 100% !important;
        table-layout: fixed !important;
        margin-bottom: 15px !important;
        border-collapse: separate !important;
        border-spacing: 0 !important;
        overflow: visible !important;
        border-right: 1pt solid #000 !important;
        border-bottom: 1pt solid #000 !important;
      }
      
      th, td {
        word-break: break-word !important;
        white-space: normal !important;
        border: none !important;
        border-top: 1pt solid #000 !important;
        border-left: 1pt solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        overflow: visible !important;
      }
      
      .AttendanceTd, .AttendanceTh {
        font-size: 10px !important;
        border: none !important;
        border-top: 1pt solid #000 !important;
        border-left: 1pt solid #000 !important;
        box-shadow: none !important;
      }
      
      .AttendanceTd {
        font-size: 10px !important;
      }
      
      .disabled-cell {
        border-top: 1pt solid #000 !important;
        border-left: 1pt solid #000 !important;
      }
  
      @page {
        size: A4 portrait;
        margin: 0.2cm;
      }
      
      body {
        zoom: 1; /* Reset zoom to normal */
      }
      
      /* Hide download button in print */
      button {
        display: none !important;
      }
      
      /* Remove grey background for disabled cells in print (borders from th, td rules) */
      .disabled-cell {
        background-color: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      /* Ensure proper spacing for sections */
      .HeaderRow {
        margin-bottom: 20px !important;
      }
      
      .Title {
        margin: 15px 0 20px 0 !important;
        text-align: center !important;
      }
      
      /* Add spacing between tables */
      .Table + .Table {
        margin-top: 15px !important;
      }
      
      /* Keep table grid lines visible when printing */
      table thead,
      table tbody tr {
        break-inside: avoid;
      }
      table td,
      table th {
        border-top: 1pt solid #000 !important;
        border-left: 1pt solid #000 !important;
        border-right: none !important;
        border-bottom: none !important;
        padding: 2px 4px !important;
      }
    }
  `}</style>
);

export default function AttendanceSheetForm() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year');
  const month = searchParams.get('month');
  const courseId = searchParams.get('courseId');
  const sessionId = searchParams.get('sessionId');
  const startParam = searchParams.get('start');
  const endParam = searchParams.get('end');

  // Parse YYYY-MM-DD as local date to avoid UTC midnight shifting to previous day (e.g. 2026-01-19 UTC -> Jan 18 in CST)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const match = String(dateStr).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match.map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(dateStr);
  };

  // Memoize periodStart/periodEnd; use local dates. Range builders (e.g. Billing/AttendanceReport) end each block on
  // Friday and advance cursor by one day → Saturday. That Saturday is already Week 4 on the prior sheet; anchor
  // the next sheet on the following Monday and extend end by one day when URL end is that paired Friday.
  const { periodStart, periodEnd } = useMemo(() => {
    if (!startParam) return { periodStart: null, periodEnd: null };
    const rawStart = parseLocalDate(startParam);
    if (!rawStart || isNaN(rawStart.getTime())) return { periodStart: null, periodEnd: null };

    const saturdayContinuationBump = rawStart.getDay() === 6;
    const periodStart = saturdayContinuationBump ? addDays(rawStart, 2) : rawStart;

    let periodEnd = null;
    if (endParam) {
      const rawEnd = parseLocalDate(endParam);
      if (rawEnd && !isNaN(rawEnd.getTime())) {
        periodEnd = saturdayContinuationBump ? addDays(rawEnd, 1) : rawEnd;
      }
    }
    if (!periodEnd) {
      periodEnd = saturdayContinuationBump ? addDays(periodStart, 26) : addDays(rawStart, 27);
    }
    return { periodStart, periodEnd };
  }, [startParam, endParam]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [instructorSignature, setInstructorSignature] = useState(null);
  const [showSignature, setShowSignature] = useState(true);

  // State for editable fields
  const [editingField, setEditingField] = useState(null); // { type: 'weekDate', week, field: 'from'/'to' } or { type: 'signatureDate', field: 'participant'/'instructor' }
  const [editValue, setEditValue] = useState('');
  const [weekDates, setWeekDates] = useState({}); // Store custom week dates: { 1: { from: '', to: '' }, 2: {...}, ... }
  const [attendanceEdits, setAttendanceEdits] = useState({}); // Store attendance edits: { 'week-day': { status, time_in, time_out, leave_reason } }
  const [signatureDates, setSignatureDates] = useState({
    participant: formatDate(new Date()),
    instructor: formatDate(new Date())
  });

  // Attendance modal state
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [currentAttendanceEdit, setCurrentAttendanceEdit] = useState(null); // { week, day }
  const [attendanceFormData, setAttendanceFormData] = useState({
    status: '',
    time_in: '',
    time_out: '',
    leave_reason: '',
    other_text: ''
  });
  const [attendanceRefreshTrigger, setAttendanceRefreshTrigger] = useState(0);
  const [updateAttendanceLoading, setUpdateAttendanceLoading] = useState(false);
  const [updateAttendanceError, setUpdateAttendanceError] = useState('');

  const student = data?.student ?? {};
  const voucher = data?.voucher ?? {};

  const todayStr = signatureDates.participant;
  const debugLoggedRef = React.useRef(false);
  const attendanceFetchKeyRef = React.useRef('');
  const fetchCancelledRef = React.useRef(false);
  const fetchInProgressRef = React.useRef(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/attendance/sheet-data/${studentId}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || 'Failed to fetch data');
        setData(json.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [studentId]);

  useEffect(() => {
    // Don't try to fetch attendance until data is loaded
    if (!data) {
      return;
    }
    
    // Try to get sessionId from student data if not in URL
    const effectiveSessionId = sessionId || (data?.student?.session);
    
    // Create a stable key to track when we should fetch
    const fetchKey = `${studentId}-${effectiveSessionId || 'no-session'}-${periodStart?.getTime() || ''}-${periodEnd?.getTime() || ''}-${data?.student?.id || ''}`;
    
    // Store the previous key before updating
    const previousKey = attendanceFetchKeyRef.current;
    
    // Only fetch if the key changed OR if previous fetch completed
    if (previousKey === fetchKey && fetchInProgressRef.current) {
      console.log('Fetch already in progress for this key, skipping...');
      return; // Already fetching for this combination
    }
    
    // Reset cancelled flag only if this is a NEW fetch (different key)
    if (previousKey !== fetchKey) {
      fetchCancelledRef.current = false;
      console.log('Starting new fetch - previous key:', previousKey, 'new key:', fetchKey);
    }
    
    attendanceFetchKeyRef.current = fetchKey;
    debugLoggedRef.current = false; // Reset debug log for new fetch
    fetchInProgressRef.current = true; // Mark fetch as in progress
    
    // Log for debugging
    if (!debugLoggedRef.current) {
      console.log('=== ATTENDANCE FETCH ATTEMPT ===');
      console.log('studentId:', studentId);
      console.log('sessionId from URL:', sessionId);
      console.log('student session from data:', data?.student?.session);
      console.log('effectiveSessionId:', effectiveSessionId);
      console.log('periodStart:', periodStart);
      console.log('periodEnd:', periodEnd);
    }
    
    // If explicit start is provided, derive year/month from it and proceed
    let effYear = year;
    let effMonth = month;
    if (periodStart) {
      const d = new Date(periodStart);
      effYear = String(d.getFullYear());
      effMonth = String(d.getMonth() + 1); // Convert to month number (1-12)
    }
    
    // If no sessionId but we have date range, try to fetch attendance from all records
    if (!effectiveSessionId && periodStart && periodEnd) {
      fetchCancelledRef.current = false;
      
      async function fetchAttendanceByStudentId() {
        try {
          // Fetch all attendance records and filter by studentId and date range
          // We'll fetch multiple pages to get all records
          let allRecords = [];
          let page = 1;
          let hasMore = true;
          
          while (hasMore && !fetchCancelledRef.current && page <= 10) { // Limit to 10 pages max
            const res = await fetch(`${API_BASE_URL}/attendance/all?page=${page}&limit=100`);
            if (!res.ok) break;
            
            const json = await res.json();
            if (!json.success || !Array.isArray(json.students)) break;
            
            // Find this student's data
            const studentData = json.students.find(
              s => String(s.id) === String(studentId) || Number(s.id) === Number(studentId)
            );
            
            if (studentData && studentData.attendance) {
              allRecords.push(...studentData.attendance);
            }
            
            // Check if there are more pages
            if (json.pagination && page >= json.pagination.totalPages) {
              hasMore = false;
            } else {
              page++;
            }
          }
          
          if (fetchCancelledRef.current) return;
          
          // Filter attendance by date range
          const filtered = allRecords.filter(record => {
            if (!record.attendance_date) return false;
            const recordDate = new Date(record.attendance_date);
            recordDate.setHours(0, 0, 0, 0);
            return recordDate >= periodStart && recordDate <= periodEnd;
          });
          
          setAttendance(filtered);
          
          // Get instructor signature if available
          const recordWithSignature = filtered.find(record => record.staff_signature);
          if (recordWithSignature) {
            setInstructorSignature(recordWithSignature.staff_signature);
          }
          
          if (!debugLoggedRef.current) {
            console.log('=== ATTENDANCE FETCHED BY STUDENTID ===');
            console.log('Total records found:', filtered.length);
            if (filtered.length > 0) {
              console.log('Sample records:', filtered.slice(0, 3));
            }
            debugLoggedRef.current = true;
          }
        } catch (err) {
          console.error('Error fetching attendance by studentId:', err);
          if (!fetchCancelledRef.current) {
            setAttendance([]);
          }
        }
      }
      
      fetchAttendanceByStudentId();
      
      return () => {
        fetchCancelledRef.current = true;
      };
    }
    
    if (!effYear || !effMonth || !effectiveSessionId) {
      // Only log once
      if (!debugLoggedRef.current) {
        console.log('=== ATTENDANCE FETCH SKIPPED (Missing Requirements) ===');
        console.log('effYear:', effYear, 'effMonth:', effMonth);
        console.log('effectiveSessionId:', effectiveSessionId);
        console.log('sessionId from URL:', sessionId);
        console.log('student session from data:', data?.student?.session);
        console.log('periodStart:', periodStart, 'periodEnd:', periodEnd);
        console.log('Note: Attempting to fetch by studentId directly if period dates available...');
        debugLoggedRef.current = true;
      }
      // Don't set empty attendance here if we're fetching by studentId
      if (!periodStart || !periodEnd) {
        setAttendance([]);
        setInstructorSignature(null);
      }
      return;
    }
    
    // Reset debug log when we actually fetch
    debugLoggedRef.current = false;
    
    // Reset cancelled flag for this fetch
    fetchCancelledRef.current = false;
    
    async function fetchAttendance() {
      console.log('=== FETCH ATTENDANCE FUNCTION CALLED ===');
      console.log('periodStart:', periodStart, 'periodEnd:', periodEnd);
      console.log('effYear:', effYear, 'effMonth:', effMonth);
      
      try {
        // If using periodStart, fetch attendance for all months in the 4-week range
        if (periodStart && periodEnd) {
          console.log('Using periodStart/periodEnd branch');
          const startDate = new Date(periodStart);
          const endDate = new Date(periodEnd);
          
          // Get all unique year-month combinations in the range
          const monthsToFetch = [];
          const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1); // Start from 1st of month
          const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
          
          while (current <= endMonth) {
            const yearStr = String(current.getFullYear());
            const monthNum = String(current.getMonth() + 1); // 1-12
            if (!monthsToFetch.some(m => m.year === yearStr && m.month === monthNum)) {
              monthsToFetch.push({ year: yearStr, month: monthNum });
            }
            current.setMonth(current.getMonth() + 1);
          }
          
          console.log('Months to fetch:', monthsToFetch);
          
          // Fetch attendance for each month and combine
          const allAttendance = [];
          let fetchedSignature = null;
          
          for (const { year: fetchYear, month: fetchMonth } of monthsToFetch) {
            if (fetchCancelledRef.current) {
              console.log('Fetch cancelled - stopping loop');
              return;
            }
            
            try {
              const url = `${API_BASE_URL}/attendance/monthly?year=${fetchYear}&month=${fetchMonth}&sessionId=${effectiveSessionId}`;
              console.log(`Fetching attendance for ${fetchMonth}/${fetchYear} from:`, url);
              
              const res = await fetch(url);
              console.log(`Response status for ${fetchMonth}/${fetchYear}:`, res.status);
              
              const json = await res.json();
              console.log(`Response for ${fetchMonth}/${fetchYear}:`, json.success, 'attendanceData length:', json.attendanceData?.length || 0);
              
              if (json.success && Array.isArray(json.attendanceData)) {
                // Convert studentId to both string and number for comparison
                const targetStudentId = String(studentId);
                const targetStudentIdNum = Number(studentId);
                
                const studentAttendance = json.attendanceData.find(
                  s => String(s.studentId) === targetStudentId || Number(s.studentId) === targetStudentIdNum
                );
                
                if (studentAttendance && studentAttendance.attendance) {
                  allAttendance.push(...studentAttendance.attendance);
                  
                  // Get instructor signature
                  if (!fetchedSignature) {
                    const recordWithSignature = studentAttendance.attendance.find(record => record.staff_signature);
                    if (recordWithSignature) {
                      fetchedSignature = recordWithSignature.staff_signature;
                    }
                  }
                }
              }
            } catch (err) {
              if (!fetchCancelledRef.current) {
                console.error(`Error fetching attendance for ${fetchMonth} ${fetchYear}:`, err);
              }
            }
          }
          
          if (!fetchCancelledRef.current) {
            console.log('=== ATTENDANCE FETCH SUMMARY ===');
            console.log('effectiveSessionId:', effectiveSessionId);
            console.log('studentId:', studentId);
            console.log('Total records fetched:', allAttendance.length);
            if (allAttendance.length > 0) {
              console.log('Sample records:', allAttendance.slice(0, 5).map(r => ({
                date: r.attendance_date,
                status: r.status,
                time_in: r.class_joined_at,
                time_out: r.class_left_at
              })));
              console.log('All fetched dates:', allAttendance.map(r => r.attendance_date));
            } else {
              console.log('WARNING: No attendance records found!');
              console.log('Months fetched:', monthsToFetch);
            }
            console.log('Setting attendance state with', allAttendance.length, 'records');
            setAttendance(allAttendance);
            if (fetchedSignature) {
              setInstructorSignature(fetchedSignature);
            }
            fetchInProgressRef.current = false; // Mark fetch as complete
          } else {
            console.log('Fetch was cancelled - not setting attendance');
            fetchInProgressRef.current = false;
          }
        } else {
          // Original single-month fetch
          console.log('Using single-month fetch branch');
          const url = `${API_BASE_URL}/attendance/monthly?year=${effYear}&month=${effMonth}&sessionId=${effectiveSessionId}`;
          console.log('Fetching from URL:', url);
          
          const res = await fetch(url);
          console.log('Response status:', res.status);
          
          const json = await res.json();
          console.log('Response success:', json.success);
          
          if (!fetchCancelledRef.current) {
            console.log('=== SINGLE MONTH FETCH RESULT ===');
            console.log('API Response success:', json.success);
            console.log('attendanceData is array:', Array.isArray(json.attendanceData));
            if (Array.isArray(json.attendanceData)) {
              console.log('Total students in response:', json.attendanceData.length);
              console.log('Looking for studentId:', studentId);
              
              // Convert studentId to both string and number for comparison
              const targetStudentId = String(studentId);
              const targetStudentIdNum = Number(studentId);
              
              // Find this student's attendance
              const studentAttendance = json.attendanceData.find(
                s => String(s.studentId) === targetStudentId || Number(s.studentId) === targetStudentIdNum
              );
              
              if (studentAttendance) {
                console.log('Found student attendance:', studentAttendance.studentName);
                console.log('Number of attendance records:', studentAttendance.attendance?.length || 0);
                if (studentAttendance.attendance && studentAttendance.attendance.length > 0) {
                  console.log('Sample records:', studentAttendance.attendance.slice(0, 3));
                }
              } else {
                console.log('WARNING: Student not found in attendanceData');
                console.log('Available studentIds:', json.attendanceData.map(s => s.studentId));
              }
              
              console.log('Setting attendance state with', studentAttendance?.attendance?.length || 0, 'records');
              setAttendance(studentAttendance ? studentAttendance.attendance : []);

              // Get instructor signature from the first attendance record with staff_signature
              if (studentAttendance && studentAttendance.attendance && studentAttendance.attendance.length > 0) {
                const recordWithSignature = studentAttendance.attendance.find(record => record.staff_signature);
                if (recordWithSignature) {
                  setInstructorSignature(recordWithSignature.staff_signature);
                }
              }
            } else {
              setAttendance([]);
            }
            fetchInProgressRef.current = false; // Mark fetch as complete
          }
        }
      } catch (err) {
        if (!fetchCancelledRef.current) {
          console.error('Error fetching attendance:', err);
          setAttendance([]);
        }
        fetchInProgressRef.current = false;
      }
    }
    
    fetchAttendance();
    
    return () => {
      // Only cancel if the key has changed (meaning a new fetch is starting)
      const currentKey = attendanceFetchKeyRef.current;
      if (currentKey !== fetchKey) {
        console.log('useEffect cleanup - fetch key changed, cancelling old fetch');
        console.log('  Old key:', fetchKey, 'Current key:', currentKey);
        fetchCancelledRef.current = true;
        fetchInProgressRef.current = false;
      } else {
        console.log('useEffect cleanup - same fetch key, allowing fetch to complete');
        // Don't cancel - let the fetch complete naturally
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, courseId, sessionId, studentId, periodStart, periodEnd, data, attendanceRefreshTrigger]);

  const hasSaturdayColumn = useMemo(() => {
    const getDisplayDateRange = () => {
      let rangeStart = null;
      let rangeEnd = null;

      if (periodStart) {
        rangeStart = normalizeAttendanceDate(periodStart);
        rangeEnd = periodEnd ? normalizeAttendanceDate(periodEnd) : null;
        if (!rangeEnd && rangeStart) {
          rangeEnd = rangeStart;
        }
      } else {
        const parsedYear = parseInt(year, 10);
        const monthNum = getMonthNumber(month);

        if (!isNaN(parsedYear) && monthNum) {
          rangeStart = normalizeAttendanceDate(new Date(parsedYear, monthNum - 1, 1));
          rangeEnd = normalizeAttendanceDate(new Date(parsedYear, monthNum, 0));
        }
      }

      return { rangeStart, rangeEnd };
    };

    const { rangeStart, rangeEnd } = getDisplayDateRange();
    const inSheetRange = date => {
      if (rangeStart && date < rangeStart) return false;
      if (rangeEnd && date > rangeEnd) return false;
      return true;
    };

    const saturdayGridDateTimes = new Set();
    const parsedYear = parseInt(year, 10);
    const fallbackYear = !isNaN(parsedYear) && parsedYear ? parsedYear : new Date().getFullYear();
    const weekCount = periodStart ? 4 : getWeeksInMonth(fallbackYear, month) || 4;

    const getSaturdayDateForWeek = (week) => {
      let weekStart;
      let weekEnd;

      if (periodStart) {
        const ps = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
        const startDow = ps.getDay();
        const firstWeekEndOffset = (6 - startDow + 7) % 7;
        const firstWeekEnd = addDays(ps, firstWeekEndOffset);
        if (week === 1) {
          weekStart = ps;
          weekEnd = firstWeekEnd;
        } else {
          const firstMondayAfter = addDays(firstWeekEnd, 2);
          weekStart = addDays(firstMondayAfter, (week - 2) * 7);
          weekEnd = addDays(weekStart, 5);
        }
      } else {
        let effYear = parseInt(year, 10);
        let effMonth = month;
        if (!effYear || isNaN(effYear) || !effMonth) {
          const fb = voucher?.training_period_start_date ? new Date(voucher.training_period_start_date) : new Date();
          if (!effYear || isNaN(effYear)) effYear = fb.getFullYear();
          if (!effMonth) effMonth = String(fb.getMonth() + 1);
        }
        const monthNum = getMonthNumber(effMonth);
        if (!effYear || !monthNum) return null;
        const firstDayOfMonth = new Date(effYear, monthNum - 1, 1);
        const lastDayOfMonth = new Date(effYear, monthNum, 0);
        const raw = new Date(effYear, monthNum - 1, 1 + (week - 1) * 7);
        weekStart = raw < firstDayOfMonth ? firstDayOfMonth : (raw > lastDayOfMonth ? lastDayOfMonth : raw);
        const rawEnd = new Date(weekStart);
        rawEnd.setDate(weekStart.getDate() + 5);
        weekEnd = rawEnd > lastDayOfMonth ? lastDayOfMonth : rawEnd;
      }

      if (!weekStart || !weekEnd) return null;

      let d = new Date(weekStart);
      while (d <= weekEnd) {
        if (d.getDay() === 6) return d;
        d = addDays(d, 1);
      }
      return null;
    };

    for (let week = 1; week <= weekCount; week++) {
      const saturday = getSaturdayDateForWeek(week);
      if (saturday) saturdayGridDateTimes.add(saturday.getTime());
    }

    if (saturdayGridDateTimes.size === 0) {
      return false;
    }

    const hasSaturdayAttendance = attendance.some(record => {
      const d = normalizeAttendanceDate(record.attendance_date || record.date);
      if (!d || d.getDay() !== 6) return false;
      if (!inSheetRange(d)) return false;
      if (!saturdayGridDateTimes.has(d.getTime())) return false;
      return true;
    });

    return hasSaturdayAttendance;
  }, [attendance, year, month, periodStart, periodEnd, voucher?.training_period_start_date]);

  const visibleDays = useMemo(() => {
    return hasSaturdayColumn ? BASE_DAYS : BASE_DAYS.filter(day => day !== 'SATURDAY');
  }, [hasSaturdayColumn]);

  useEffect(() => {
    if (!attendance || attendance.length === 0) {
      return;
    }

    const allDates = attendance
      .map(record => normalizeAttendanceDate(record.attendance_date || record.date))
      .filter(Boolean);

    if (allDates.length === 0) {
      return;
    }

    const getDisplayDateRange = () => {
      let rangeStart = null;
      let rangeEnd = null;

      if (periodStart) {
        rangeStart = normalizeAttendanceDate(periodStart);
        rangeEnd = periodEnd ? normalizeAttendanceDate(periodEnd) : null;
        if (!rangeEnd && rangeStart) {
          rangeEnd = rangeStart;
        }
      } else {
        const parsedYear = parseInt(year, 10);
        const monthNum = getMonthNumber(month);

        if (!isNaN(parsedYear) && monthNum) {
          rangeStart = normalizeAttendanceDate(new Date(parsedYear, monthNum - 1, 1));
          rangeEnd = normalizeAttendanceDate(new Date(parsedYear, monthNum, 0));
        }
      }

      return { rangeStart, rangeEnd };
    };

    const { rangeStart, rangeEnd } = getDisplayDateRange();

    const inSheetRange = date => {
      if (rangeStart && date < rangeStart) return false;
      if (rangeEnd && date > rangeEnd) return false;
      return true;
    };

    // Only dates that correspond to a real Mon–Sat cell on this form (same as getDateForCell).
    // In-range DB rows on days outside the 4 rendered weeks (e.g. Wed 04/15 while Week 4 ends Sat 04/11)
    // must not drive the signature line.
    const gridDateTimes = new Set();
    const parsedYear = parseInt(year, 10);
    const weekCount = periodStart
      ? 4
      : getWeeksInMonth(!isNaN(parsedYear) && parsedYear ? parsedYear : new Date().getFullYear(), month) || 4;
    for (let week = 1; week <= weekCount; week++) {
      for (const day of visibleDays) {
        const ymd = getDateForCell(week, day);
        if (!ymd) continue;
        const nd = normalizeAttendanceDate(ymd);
        if (nd) gridDateTimes.add(nd.getTime());
      }
    }

    const onDisplayedGrid = date => {
      if (!date) return false;
      if (gridDateTimes.size === 0) return inSheetRange(date);
      return gridDateTimes.has(date.getTime());
    };

    const markedDates = attendance
      .filter(recordHasMarkedAttendanceForSignature)
      .map(record => normalizeAttendanceDate(record.attendance_date || record.date))
      .filter(d => d && onDisplayedGrid(d) && inSheetRange(d));

    let latestDate = null;
    if (markedDates.length > 0) {
      latestDate = markedDates.reduce(
        (latest, current) => (current > latest ? current : latest),
        markedDates[0]
      );
    }

    if (!latestDate) {
      const onGridInRange = allDates.filter(d => onDisplayedGrid(d) && inSheetRange(d));
      const candidateDates = onGridInRange.length > 0 ? onGridInRange : allDates.filter(inSheetRange);
      const finalPool = candidateDates.length > 0 ? candidateDates : allDates;
      latestDate = finalPool.reduce(
        (latest, current) => (current > latest ? current : latest),
        finalPool[0]
      );
    }

    const formatted = formatDate(latestDate);

    setSignatureDates(prev => {
      if (prev.participant === formatted && prev.instructor === formatted) {
        return prev;
      }

      return {
        participant: formatted,
        instructor: formatted
      };
    });
  }, [attendance, year, month, periodStart, periodEnd, visibleDays]);

  if (error) return <ErrorMsg>{error}</ErrorMsg>;
  if (!loading && !data) return <ErrorMsg>No data found.</ErrorMsg>;

  // Helper to handle PDF download with custom filename
  const handleDownloadPDF = () => {
    // Get month name from month parameter
    const getMonthName = (monthParam) => {
      if (!monthParam) return 'Unknown';
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthNum = parseInt(monthParam);
      if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
        return monthNames[monthNum - 1];
      }
      // If it's already a month name, capitalize it
      return monthParam.charAt(0).toUpperCase() + monthParam.slice(1).toLowerCase();
    };

    // Build the filename
    const studentName = `${student.first_name || 'Unknown'}_${student.last_name || 'Student'}`;
    const monthName = getMonthName(month);
    const courseName = (voucher?.program_name || 'Course').replace(/[^a-zA-Z0-9]/g, '_');
    const sessionName = voucher?.session_name || searchParams.get('session') || 'Session';

    const filename = `${studentName}_${monthName}_${courseName}_${sessionName}`;

    // Set document title to the filename (this will be used as the PDF filename)
    const originalTitle = document.title;
    document.title = filename;

    // Trigger print
    window.print();

    // Restore original title after a short delay
    setTimeout(() => {
      document.title = originalTitle;
    }, 100);
  };

  // Helper to get week number in month (1-based)
  function getWeekOfMonth(date) {
    const d = new Date(date);
    const day = d.getDate();
    const firstDayOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
    const firstDayWeekday = firstDayOfMonth.getDay();
    const week = Math.floor((day + firstDayWeekday - 1) / 7) + 1;
   
    
    return week;
  }

  // Helper to get total weeks to render
  function getWeeksInMonth(year, month) {
    // If a start date is provided via URL, always render 4 weeks
    if (periodStart) return 4;
    const monthNum = getMonthNumber(month);
    if (!year || !monthNum) return 4;
    const firstDay = new Date(year, monthNum - 1, 1);
    const lastDay = new Date(year, monthNum, 0);
    const firstWeek = getWeekOfMonth(firstDay);
    const lastWeek = getWeekOfMonth(lastDay);
    return Math.min(lastWeek - firstWeek + 1, 5);
  }

  // Normalize an attendance date string/value to a Date object at midnight
  function normalizeAttendanceDate(dateInput) {
    if (!dateInput) return null;

    let attDate = null;

    if (dateInput instanceof Date && !isNaN(dateInput)) {
      attDate = new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    } else if (typeof dateInput === 'string') {
      const dateStr = dateInput.trim();
      if (!dateStr) return null;

      if (dateStr.includes('T') || dateStr.includes('Z')) {
        const datePart = dateStr.split('T')[0];
        const [attYear, attMonth, attDay] = datePart.split('-').map(Number);
        if (!isNaN(attYear) && !isNaN(attMonth) && !isNaN(attDay)) {
          attDate = new Date(attYear, attMonth - 1, attDay);
        }
      } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [attYear, attMonth, attDay] = dateStr.split('-').map(Number);
        if (!isNaN(attYear) && !isNaN(attMonth) && !isNaN(attDay)) {
          attDate = new Date(attYear, attMonth - 1, attDay);
        }
      } else {
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) {
          attDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        }
      }
    }

    if (!attDate || isNaN(attDate)) return null;
    attDate.setHours(0, 0, 0, 0);
    return attDate;
  }

  // Helper to format time as 10:00 AM (12-hour format)
  function formatTime(t) {
    if (!t) return '';
    const [h, m] = t.split(':');
    const date = new Date(1970, 0, 1, h, m);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  // Helper to calculate total hours between two times
  function getHours(joined, left) {
    if (!joined || !left) return 0;
    const [jh, jm] = joined.split(':');
    const [lh, lm] = left.split(':');
    let start = new Date(1970, 0, 1, jh, jm);
    let end = new Date(1970, 0, 1, lh, lm);
    if (end < start) end.setDate(end.getDate() + 1);
    return (end - start) / (1000 * 60 * 60);
  }

  // Helper to generate student initials
  function getStudentInitials(student) {
    if (!student) return '';
    const firstName = student.first_name || student.firstName || '';
    const lastName = student.last_name || student.lastName || '';

    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();

    return `${firstInitial}${lastInitial}`;
  }

  // Helper to convert month name to number
  function getMonthNumber(monthStr) {
    if (!monthStr) return null;

    // If it's already a number, return it
    const monthNum = parseInt(monthStr);
    if (!isNaN(monthNum)) return monthNum;

    // Convert month name to number
    const monthNames = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];

    const monthIndex = monthNames.indexOf(monthStr.toLowerCase());
    return monthIndex !== -1 ? monthIndex + 1 : null;
  }

  // Helper to get week date range
  function getWeekDateRange(week, year, month) {
    const includeSaturday = hasSaturdayColumn;
    // If using explicit 4-week period, compute from periodStart (training period start = Week 1 "From")
    if (periodStart) {
      const ps = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
      const startDow = ps.getDay(); // 0=Sun ... 6=Sat
      // First week: from training period start through the last displayed day (Fri/Sat)
      const weekEndDow = includeSaturday ? 6 : 5;
      const firstWeekEndOffset = (weekEndDow - startDow + 7) % 7;
      const firstWeekEnd = addDays(ps, firstWeekEndOffset);

      if (week === 1) {
        return { from: formatDate(ps), to: formatDate(firstWeekEnd) };
      }

      // Subsequent weeks start on Monday after the first displayed week end
      const daysUntilMonday = ((8 - firstWeekEnd.getDay()) % 7) || 7;
      const firstMondayAfter = addDays(firstWeekEnd, daysUntilMonday);
      const start = addDays(firstMondayAfter, (week - 2) * 7);
      const end = addDays(start, includeSaturday ? 5 : 4); // Mon -> Sat or Mon -> Fri
      return { from: formatDate(start), to: formatDate(end) };
    }
    const monthNum = getMonthNumber(month);
    if (!year || !monthNum || isNaN(year)) {
      return { from: '', to: '' };
    }
    const firstDayOfMonth = new Date(year, monthNum - 1, 1);
    const lastDayOfMonth = new Date(year, monthNum, 0);
    const daysFromFirstDay = (week - 1) * 7;
    const weekStart = new Date(year, monthNum - 1, 1 + daysFromFirstDay);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + (includeSaturday ? 5 : 4)); // Mon -> Sat or Mon -> Fri
    const constrainedStart = weekStart > lastDayOfMonth ? lastDayOfMonth : (weekStart < firstDayOfMonth ? firstDayOfMonth : weekStart);
    const constrainedEnd = weekEnd > lastDayOfMonth ? lastDayOfMonth : weekEnd;
    return { from: formatDate(constrainedStart), to: formatDate(constrainedEnd) };
  }

  // Compute the exact calendar date for a (week, day) cell — works directly
  // with Date objects to avoid timezone issues from string roundtrips.
  function getDateForCell(week, day) {
    const includeSaturday = hasSaturdayColumn;
    const targetDow = DAY_NAME_TO_DOW[(day || '').toUpperCase()];
    if (targetDow === undefined) return null;

    let weekStart, weekEnd;

    if (periodStart) {
      // Normalize periodStart to local midnight to avoid UTC-offset drift
      const ps = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate());
      const startDow = ps.getDay();
      const weekEndDow = includeSaturday ? 6 : 5;
      const firstWeekEndOffset = (weekEndDow - startDow + 7) % 7;
      const firstWeekEnd = addDays(ps, firstWeekEndOffset);

      if (week === 1) {
        weekStart = ps;
        weekEnd = firstWeekEnd;
      } else {
        const daysUntilMonday = ((8 - firstWeekEnd.getDay()) % 7) || 7;
        const firstMondayAfter = addDays(firstWeekEnd, daysUntilMonday);
        weekStart = addDays(firstMondayAfter, (week - 2) * 7);
        weekEnd = addDays(weekStart, includeSaturday ? 5 : 4);
      }
    } else {
      let effYear = parseInt(year, 10);
      let effMonth = month;
      if (!effYear || isNaN(effYear) || !effMonth) {
        const fb = voucher?.training_period_start_date ? new Date(voucher.training_period_start_date) : new Date();
        if (!effYear || isNaN(effYear)) effYear = fb.getFullYear();
        if (!effMonth) effMonth = String(fb.getMonth() + 1);
      }
      const monthNum = getMonthNumber(effMonth);
      if (!effYear || !monthNum) return null;
      const firstDayOfMonth = new Date(effYear, monthNum - 1, 1);
      const lastDayOfMonth = new Date(effYear, monthNum, 0);
      const raw = new Date(effYear, monthNum - 1, 1 + (week - 1) * 7);
      weekStart = raw < firstDayOfMonth ? firstDayOfMonth : (raw > lastDayOfMonth ? lastDayOfMonth : raw);
      const rawEnd = new Date(weekStart);
      rawEnd.setDate(weekStart.getDate() + (includeSaturday ? 5 : 4));
      weekEnd = rawEnd > lastDayOfMonth ? lastDayOfMonth : rawEnd;
    }

    if (!weekStart || !weekEnd) return null;

    let d = new Date(weekStart);
    while (d <= weekEnd) {
      if (d.getDay() === targetDow) {
        return format(d, 'yyyy-MM-dd');
      }
      d = addDays(d, 1);
    }
    return null;
  }
  
  // Build attendance grid: week -> day -> record
  function getAttendanceForCell(week, day) {
    // Get all matching records for this week/day
    const matchingRecords = attendance.filter(a => {
      const dateStr = a.attendance_date || a.date;
      if (!dateStr) return false;
      
      const attDate = normalizeAttendanceDate(dateStr);
      if (!attDate) return false;
      
      const attDayName = attDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();

      // Determine week index: if custom period, use computed week range boundaries
      let attWeek;
      let inWeekRange = false;
      
      if (periodStart) {
        // Build the MM/DD/YY range for this displayed week, then normalize to full Date
        const range = getWeekDateRange(week, parseInt(year || new Date(periodStart).getFullYear()), month || '');
        
        const toFullDate = (mmddyy) => {
          if (!mmddyy) return null;
          const [mm, dd, yy] = mmddyy.split('/');
          const fullYear = yy.length === 2 ? Number(`20${yy}`) : Number(yy);
          const date = new Date(fullYear, Number(mm) - 1, Number(dd));
          date.setHours(0, 0, 0, 0); // Normalize to midnight
          return date;
        };
        const from = toFullDate(range.from);
        const to = toFullDate(range.to);
        
        // Null check for from/to
        if (!from || !to) {
          return false;
        }
        
        // Only keep records whose date falls within this week's from/to (inclusive)
        inWeekRange = (attDate >= from && attDate <= to);
        
        if (!inWeekRange) return false;
        attWeek = week;
      } else {
        attWeek = getWeekOfMonth(attDate);
        inWeekRange = (attWeek === week);
        if (!inWeekRange) return false;
      }

      // Match the specific day
      const dayMatch = attDayName === day;

      return inWeekRange && dayMatch;
    });
    
    // If multiple records, prioritize Present, then Others, then Absent, then On Leave/Leave
    if (matchingRecords.length > 1) {
      const priority = { 'Present': 1, 'Others': 2, 'Absent': 3, 'On Leave': 4, 'Leave': 4 };
      matchingRecords.sort((a, b) => (priority[a.status] || 5) - (priority[b.status] || 5));
    }
    
    return matchingRecords[0] || null;
  }

  // For each week, sum total hours
  function getTotalHoursForWeek(week) {
    let total = 0;
    visibleDays.forEach(day => {
      const key = `${week}-${day}`;
      const rec = getAttendanceForCell(week, day);
      const editedData = attendanceEdits[key];

      // Check edited data first, then fall back to original record
      if (editedData) {
        if (editedData.status === 'Present' && editedData.time_in && editedData.time_out) {
          total += getHours(editedData.time_in, editedData.time_out);
        }
      } else if (rec && rec.status === 'Present') {
        total += getHours(rec.class_joined_at, rec.class_left_at);
      }
    });
    return total;
  }

  // Helper to convert MM/DD/YY to YYYY-MM-DD for date picker
  const convertToDatePickerFormat = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert from MM/DD/YY to YYYY-MM-DD
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) {
        year = '20' + year;
      }
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  // Helper to convert YYYY-MM-DD back to MM/DD/YY for display
  const convertFromDatePickerFormat = (dateStr) => {
    if (!dateStr) return '';
    // If in YYYY-MM-DD format, convert to MM/DD/YY
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = dateStr.split('-');
      const year = parts[0].slice(-2);
      const month = parts[1];
      const day = parts[2];
      return `${month}/${day}/${year}`;
    }
    return dateStr;
  };

  // Edit handlers
  const handleFieldClick = (fieldType, params) => {
    if (fieldType === 'weekDate') {
      const { week, field } = params;
      const weekDatesForWeek = getWeekDateRange(week, parseInt(year), month);
      const currentValue = weekDates[week]?.[field] || weekDatesForWeek[field];
      setEditingField({ type: 'weekDate', week, field });
      setEditValue(convertToDatePickerFormat(currentValue));
    } else if (fieldType === 'signatureDate') {
      const { field } = params;
      setEditingField({ type: 'signatureDate', field });
      setEditValue(convertToDatePickerFormat(signatureDates[field]));
    } else if (fieldType === 'attendance') {
      const { week, day } = params;
      // Prevent editing cells for days that don't exist in this week's date range
      if (!getDateForCell(week, day)) return;
      const key = `${week}-${day}`;
      const rec = getAttendanceForCell(week, day);
      const existingEdit = attendanceEdits[key];

      // Open modal with current data
      setCurrentAttendanceEdit({ week, day });
      const mapDbStatus = (s) => {
        if (!s) return '';
        if (s === 'On Leave') return 'Leave';
        if (['Present', 'Absent', 'Leave', 'Others'].includes(s)) return s;
        return '';
      };
      setAttendanceFormData({
        status: existingEdit?.status || mapDbStatus(rec?.status),
        time_in: existingEdit?.time_in || rec?.class_joined_at || '',
        time_out: existingEdit?.time_out || rec?.class_left_at || '',
        leave_reason: existingEdit?.leave_reason || rec?.leave_reason || '',
        other_text: existingEdit?.other_text || rec?.notes || ''
      });
      setShowAttendanceModal(true);
    }
  };

  const handleFieldSave = () => {
    if (!editingField) return;

    if (editingField.type === 'weekDate') {
      const { week, field } = editingField;
      setWeekDates(prev => ({
        ...prev,
        [week]: {
          ...prev[week],
          [field]: convertFromDatePickerFormat(editValue)
        }
      }));
    } else if (editingField.type === 'signatureDate') {
      const { field } = editingField;
      setSignatureDates(prev => ({
        ...prev,
        [field]: convertFromDatePickerFormat(editValue)
      }));
    }

    setEditingField(null);
    setEditValue('');
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleFieldSave();
    } else if (e.key === 'Escape') {
      handleFieldCancel();
    }
  };

  // Attendance modal handlers
  const handleAttendanceModalClose = () => {
    setShowAttendanceModal(false);
    setCurrentAttendanceEdit(null);
    setAttendanceFormData({
      status: '',
      time_in: '',
      time_out: '',
      leave_reason: '',
      other_text: ''
    });
  };

  const handleAttendanceFormChange = (field, value) => {
    setAttendanceFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAttendanceSubmit = () => {
    if (!currentAttendanceEdit) return;

    const { week, day } = currentAttendanceEdit;
    const key = `${week}-${day}`;

    setAttendanceEdits(prev => ({
      ...prev,
      [key]: {
        status: attendanceFormData.status,
        time_in: attendanceFormData.time_in,
        time_out: attendanceFormData.time_out,
        leave_reason: attendanceFormData.leave_reason,
        other_text: attendanceFormData.other_text
      }
    }));

    handleAttendanceModalClose();
  };

  const handleUpdateAttendance = async () => {
    setUpdateAttendanceError('');
    const keys = Object.keys(attendanceEdits);
    if (keys.length === 0) {
      setUpdateAttendanceError('No attendance changes to save.');
      return;
    }
    if (!studentId) {
      setUpdateAttendanceError('Student not found. Please reload the page.');
      return;
    }
    let records = [];
    try {
      for (const key of keys) {
        const parts = key.split('-');
        const week = parseInt(parts[0], 10);
        const day = parts.length >= 2 ? parts.slice(1).join('-') : '';
        if (!day || isNaN(week)) continue;
        const attendanceDate = getDateForCell(week, day);
        if (!attendanceDate) continue;
        const edited = attendanceEdits[key];
        if (!edited || !edited.status) continue;
        records.push({
          attendanceDate,
          status: edited.status,
          class_joined_at: edited.time_in || null,
          class_left_at: edited.time_out || null,
          leave_reason: edited.leave_reason || null,
          notes: edited.other_text || null
        });
      }
    } catch (err) {
      setUpdateAttendanceError(err.message || 'Failed to prepare attendance data.');
      return;
    }
    if (records.length === 0) {
      setUpdateAttendanceError('Could not determine dates for the edited cells. Try reopening the sheet and saving again.');
      return;
    }
    setUpdateAttendanceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/attendance/individual/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ studentId, records })
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed to update attendance');
      setAttendanceEdits({});
      setAttendanceRefreshTrigger(t => t + 1);
    } catch (err) {
      setUpdateAttendanceError(err.message || 'Failed to update attendance');
    } finally {
      setUpdateAttendanceLoading(false);
    }
  };

  return (
    <>
      <PrintStyle />
      <Container>
        <HeaderRow>
          <Logo src={process.env.PUBLIC_URL + '/logo_attendance_sheet.png'} alt="Logo" />
        </HeaderRow>
        <Title>ITA TRAINING ATTENDANCE SHEET</Title>
      
        <Table style={{ display: 'inline-table' }}>
          <tbody>
            <Row>
              <Th style={{ textAlign: 'left' }}>Participant Name</Th>
              <Td>{loading ? <LoadingField>Loading...</LoadingField> : `${student.first_name || ''} ${student.last_name || ''}`.trim() || '—'}</Td>
              <Th style={{ textAlign: 'left' }}>Last Four SSN</Th>
              <Td>{loading ? <LoadingField>Loading...</LoadingField> : (student.last_four_ssn || '')}</Td>
            </Row>
            <Row>
              <Th style={{ textAlign: 'left' }}>Referring WIOA Provider</Th>
              <Td colSpan={3}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.referring_wia_provider || '')}</Td>
            </Row>
          </tbody>
        </Table>
        <Table style={{ display: 'inline-table' }}>
          <tbody>
            <Row>
              <Th style={{ textAlign: 'left' }}>Career Coach Manager</Th>
              <Td>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.case_manager_name || '')}</Td>
              <Th style={{ textAlign: 'left' }}>Telephone</Th>
              <Td>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.case_manager_telephone || '')}</Td>
            </Row>
            <Row>
              <Th style={{ textAlign: 'left' }}>Training Provider</Th>
              <Td colSpan={3}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.training_provider || '')}</Td>
            </Row>
            <Row>
              <Th style={{ textAlign: 'left' }}>Instructor Name</Th>
              <Td colSpan={3}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.instructor_name || '')}</Td>
            </Row>
            <Row>
              <Th style={{ textAlign: 'left' }}>Program/Class Name</Th>
              <Td colSpan={3}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.program_name || '')}</Td>
            </Row>
          </tbody>
        </Table>
        <Table style={{ display: 'inline-table' }}>
          <tbody>
            <Row>
              <Td colSpan={8} style={{ padding: "10px 20px" }}>
                <div style={{ margin: "0px", fontWeight: 600 }}>
                  Student must initial within the box to indicate number of days attended. Both student and instructor must sign to verify attendance. <span style={{ fontWeight: 'bold', fontStyle: 'italic' }}>To be used for instructional class time only.</span>
                </div>
              </Td>
            </Row>
          </tbody>
        </Table>
        <Table style={{ display: 'inline-table' }}>
          <tbody>
            <Row>
              <Td style={{ textAlign: 'center' }}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.training_period_start_date ? formatDate(voucher.training_period_start_date) : '')}</Td>
              <Th rowSpan={2} style={{ textAlign: 'center', verticalAlign: 'middle' }}>Through</Th>
              <Td style={{ textAlign: 'center' }}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.training_period_end_date ? formatDate(voucher.training_period_end_date) : '')}</Td>

            </Row>
            <Row>
              <Th style={{ fontWeight: 'normal' }}>(Training Period Start)</Th>
              <Th style={{ fontWeight: 'normal' }}>(Training Period End)</Th>
            </Row>
          </tbody>
        </Table>
        <Table style={{ display: 'inline-table' }}>
          <thead>
            <Row>
              <AttendanceTh rowSpan={1} style={{ width: '140px' }} >WEEKS</AttendanceTh>
              {visibleDays.map(day => <AttendanceTh key={day} rowSpan={1}>{day}</AttendanceTh>)}
              <AttendanceTh rowSpan={1} style={{ width: '100px' }} >Total Weekly Hours</AttendanceTh>
            </Row>
          </thead>
          <tbody>
            {Array.from({ length: getWeeksInMonth(parseInt(year), month) }, (_, i) => i + 1).map(week => {
              const weekDatesForWeek = getWeekDateRange(week, parseInt(year), month);
              return (
                <Row key={week}>
                  <AttendanceTd style={{ verticalAlign: 'top', padding: '0px', minWidth: '100px' }}>
                    <div style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '4px', paddingTop: '5px', fontSize: '13px' }}>Week {week}</div>
                    <div style={{ fontSize: '0.75em', textAlign: 'left' }}>
                      <div style={{ display: 'flex', borderBottom: '1px solid black', borderTop: '1px solid black', paddingLeft: '5px' }}>
                        <div style={{ borderRight: '1px solid black', width: '36%' }}>
                          <p style={{ margin: '2px 0', fontSize: '13px' }}>From:</p>
                        </div>
                        <div style={{ marginLeft: '5px', marginTop: '5px', fontSize: '13px'}}>
                          {editingField?.type === 'weekDate' && editingField.week === week && editingField.field === 'from' ? (
                            <EditInput
                              type="date"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={handleFieldSave}
                              autoFocus
                              style={{ fontSize: '11px', padding: '2px 4px' }}
                            />
                          ) : (
                            <EditableField onClick={() => !loading && handleFieldClick('weekDate', { week, field: 'from' })}>
                              {loading ? <LoadingField>Loading...</LoadingField> : (weekDates[week]?.from || weekDatesForWeek.from)}
                            </EditableField>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', paddingLeft: '5px' }}>
                        <div style={{ borderRight: '1px solid black', width: '36%' }}>
                          <p style={{ margin: '2px 0', fontSize: '13px' }}>To:</p>
                        </div>
                        <div style={{ marginLeft: '5px', marginTop: '5px', fontSize: '13px' }}>
                          {editingField?.type === 'weekDate' && editingField.week === week && editingField.field === 'to' ? (
                            <EditInput
                              type="date"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={handleFieldSave}
                              autoFocus
                              style={{ fontSize: '11px', padding: '2px 4px' }}
                            />
                          ) : (
                            <EditableField onClick={() => !loading && handleFieldClick('weekDate', { week, field: 'to' })}>
                              {loading ? <LoadingField>Loading...</LoadingField> : (weekDates[week]?.to || weekDatesForWeek.to)}
                            </EditableField>
                          )}
                        </div>
                      </div>
                    </div>
                  </AttendanceTd>
                  {visibleDays.map((day, i) => {
                    if (loading) {
                      return (
                        <AttendanceTd key={i} style={{ position: 'relative', height: '60px' }}>
                          <LoadingField>Loading...</LoadingField>
                        </AttendanceTd>
                      );
                    }
                    const cellDateExists = !!getDateForCell(week, day);
                    const rec = getAttendanceForCell(week, day);
                    const key = `${week}-${day}`;
                    const editedData = attendanceEdits[key];
                    const displayStatus = editedData?.status || rec?.status || '';
                    const displayTimeIn = editedData?.time_in || rec?.class_joined_at || '';
                    const displayTimeOut = editedData?.time_out || rec?.class_left_at || '';
                    const displayLeaveReason = editedData?.leave_reason || rec?.leave_reason || '';
                    const displayOtherText = editedData?.other_text || rec?.notes || '';
                    return (
                      <AttendanceTd key={i} className={!cellDateExists ? 'disabled-cell' : ''} style={{ position: 'relative', height: '60px', ...(!cellDateExists ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}) }}>

                        {displayStatus === 'Present' && displayTimeIn && displayTimeOut && (
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: '4px',
                            fontSize: '12px',
                            color: 'black',
                            fontWeight: '500'
                          }}>
                            {formatTime(displayTimeIn)} - {formatTime(displayTimeOut)}
                          </div>
                        )}
                        {(displayStatus === 'Leave' || displayStatus === 'On Leave') && displayLeaveReason && (
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: '4px',
                            fontSize: '12px',
                            color: 'black',
                          }}>
                            {displayLeaveReason}
                          </div>
                        )}
                        {displayStatus === 'Absent' && (
                          <div style={{
                            position: 'absolute',
                            top: '4px',
                            left: '6px',
                            fontSize: '12px',
                            color: 'black',
                          }}>
                            Absent
                          </div>
                        )}
                        {displayStatus === 'Others' && (
                          <div style={{ position: 'absolute',
                            top: '4px',
                            left: '6px',
                            fontSize: '12px',
                            color: 'black',
                            fontWeight: '500',
                          }}>
                            {displayOtherText || 'Others'}
                          </div>
                        )}

                        <EditableField
                          onClick={() => cellDateExists && handleFieldClick('attendance', { week, day })}
                          style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '4px',
                            fontSize: displayStatus === 'Others' ? '0.85em' : '1.1em',
                            fontFamily: displayStatus === 'Present' ? 'cursive' : 'inherit',
                            color: "black",
                            minWidth: '30px',
                            minHeight: '30px',
                            display: displayStatus === 'Others' ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '2px',
                            ...(!cellDateExists ? { cursor: 'not-allowed' } : {})
                          }}
                        >
                          {displayStatus === 'Present' ? getStudentInitials(student) :
                             (displayStatus === 'Leave' || displayStatus === 'On Leave') ? 'Leave' :
                             displayStatus === 'Others' ? (displayOtherText || 'Others') : ''}
                        </EditableField>
                      </AttendanceTd>
                    );
                  })}
                  <AttendanceTd style={{  verticalAlign: 'top',fontSize: '14px', }}>
                    {loading ? <LoadingField>Loading...</LoadingField> : (() => {
                      const total = getTotalHoursForWeek(week);
                      if (total > 0) {
                        const rounded = Math.round(total * 10) / 10; // one decimal place
                        return `${rounded.toFixed(1)} hrs`;
                      }
                      return '';
                    })()}
                  </AttendanceTd>
                </Row>
              );
            })}
          </tbody>
        </Table>
        {Object.keys(attendanceEdits).length > 0 && (
          <div style={{  marginBottom: 12, textAlign: 'right' }}>
            {updateAttendanceError && (
              <div style={{ color: '#b00', marginBottom: 8, fontSize: '0.9rem' }}>{updateAttendanceError}</div>
            )}
            <UpdateAttendanceButton
              onClick={handleUpdateAttendance}
              disabled={updateAttendanceLoading}
            >
              {updateAttendanceLoading ? 'Updating…' : 'Update Attendance'}
            </UpdateAttendanceButton>
          </div>
        )}
        <Table style={{ display: 'inline-table' }}>
          <tbody>
            <Row>
              <Th style={{ width: '300px', textAlign: 'left', paddingLeft: '20px' }}>Participant Signature</Th>
              <Td style={{ paddingBottom: 0 }}><SignatureBox>{loading ? <LoadingField>Loading...</LoadingField> : (showSignature && student.signature ? <img className="signature-img" src={student.signature} alt="Signature" style={{ maxHeight: 60, height: 'auto', width: 'auto', objectFit: 'contain', imageRendering: 'auto', filter: 'none', mixBlendMode: 'normal' }} /> : '')}</SignatureBox></Td>
              <Th style={{ width: '80px' }}>Date</Th>
              <Td style={{ paddingLeft: '10px' }}>
                {loading ? <LoadingField>Loading...</LoadingField> : (editingField?.type === 'signatureDate' && editingField.field === 'participant' ? (
                  <EditInput
                    type="date"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleFieldSave}
                    autoFocus
                    style={{ fontSize: '12px', padding: '4px' }}
                  />
                ) : (
                  <EditableField onClick={() => handleFieldClick('signatureDate', { field: 'participant' })}>
                    {signatureDates.participant}
                  </EditableField>
                ))}
              </Td>
            </Row>
            <Row>
              <Th style={{ width: '300px', textAlign: 'left', padding: '10px 0 10px 20px' }}>Instructor Name</Th>
              <Td colSpan={3}>{loading ? <LoadingField>Loading...</LoadingField> : (voucher?.instructor_name || '')}</Td>
            </Row>
            <Row>
              <Th style={{ width: '350px', textAlign: 'left', paddingLeft: '20px' }}>Instructor or School Officials Signature</Th>
              <Td style={{ paddingBottom: 0 }}><SignatureBox>{loading ? <LoadingField>Loading...</LoadingField> : (instructorSignature ? <img className="signature-img" src={instructorSignature} alt="Instructor Signature" style={{ maxHeight: 60, height: 'auto', width: 'auto', objectFit: 'contain', mixBlendMode: 'normal', filter: 'none' }} /> : '')}</SignatureBox></Td>
              <Th style={{ width: '80px' }}>Date</Th>
              <Td style={{ paddingRight: '10px' }}>
                {loading ? <LoadingField>Loading...</LoadingField> : (editingField?.type === 'signatureDate' && editingField.field === 'instructor' ? (
                  <EditInput
                    type="date"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleFieldSave}
                    autoFocus
                    style={{ fontSize: '12px', padding: '4px' }}
                  />
                ) : (
                  <EditableField onClick={() => handleFieldClick('signatureDate', { field: 'instructor' })}>
                    {signatureDates.instructor}
                  </EditableField>
                ))}
              </Td>
            </Row>
          </tbody>
        </Table>
        <Notice>
          <p style={{ marginLeft: '20px', marginTop: 0, lineHeight: '22px' }}>Notice: This document should be used to track attendance for all students who have accessed an ITA. However, for Training Providers with more than 10 current enrollments, this information may be summarized, and the School Official may attest to the participant's attendance on their behalf.
          </p>
        </Notice>
        <ButtonContainer>
          <DownloadButton onClick={handleDownloadPDF} disabled={loading}>
           🖨️
           Print
          </DownloadButton>
          <ToggleSignatureButton
            showSignature={showSignature}
            onClick={() => !loading && setShowSignature(!showSignature)}
            disabled={loading}
          >
            {showSignature ? 'Hide Signature' : 'Show Signature'}
          </ToggleSignatureButton>
        </ButtonContainer>
      </Container>

      {/* Attendance Edit Modal */}
      {showAttendanceModal && (
        <AttendanceModal onClick={handleAttendanceModalClose}>
          <AttendanceModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit Attendance - {currentAttendanceEdit?.day}</ModalTitle>

            <FormGroup>
              <Label>Status *</Label>
              <Select
                value={attendanceFormData.status}
                onChange={(e) => handleAttendanceFormChange('status', e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
                <option value="Leave">Leave</option>
                <option value="Others">Others</option>
              </Select>
            </FormGroup>

            {attendanceFormData.status === 'Present' && (
              <TimeInputContainer>
                <FormGroup>
                  <Label>Time In</Label>
                  <Input
                    type="time"
                    value={attendanceFormData.time_in}
                    onChange={(e) => handleAttendanceFormChange('time_in', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Time Out</Label>
                  <Input
                    type="time"
                    value={attendanceFormData.time_out}
                    onChange={(e) => handleAttendanceFormChange('time_out', e.target.value)}
                  />
                </FormGroup>
              </TimeInputContainer>
            )}

            {attendanceFormData.status === 'Leave' && (
              <FormGroup>
                <Label>Leave Reason</Label>
                <Input
                  type="text"
                  value={attendanceFormData.leave_reason}
                  onChange={(e) => handleAttendanceFormChange('leave_reason', e.target.value)}
                  placeholder="Enter reason for leave"
                />
              </FormGroup>
            )}

            {attendanceFormData.status === 'Others' && (
              <FormGroup>
                <Label>Custom Text</Label>
                <Input
                  type="text"
                  value={attendanceFormData.other_text}
                  onChange={(e) => handleAttendanceFormChange('other_text', e.target.value)}
                  placeholder="Enter custom text"
                />
              </FormGroup>
            )}

            <ModalButtonGroup>
              <CancelButton onClick={handleAttendanceModalClose}>
                Cancel
              </CancelButton>
              <SubmitButton onClick={handleAttendanceSubmit}>
                Save
              </SubmitButton>
            </ModalButtonGroup>
          </AttendanceModalContent>
        </AttendanceModal>
      )}
    </>
  );
} 